import { Box, Grid } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { getBackendErrors, getErrorMessage } from "api/errors";
import { useEvents } from "api/events";
import { FormButtons } from "components/FormControls/FormButtons";
import { FormSection } from "components/FormControls/FormSection";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { TextInput } from "components/FormControls/TextInput";
import { Toggle } from "components/FormControls/Toggle";
import { ReactComponent as InfoCircleIcon } from "components/Icons/info-circle.svg";
import { LeaveSiteGuard } from "components/Tooltips/LeaveSiteGuard";
import { Tooltip } from "components/Tooltips/Tooltip";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { Form, Formik, FormikErrors } from "formik";
import { useMessage } from "hooks/intl";
import { useNavigate, useParams } from "react-router-dom";
import { EventSchemaPayload, SchemaAttribute } from "types/customEvents";
import { backendErrors } from "utilities/backendErrors";
import { focusError } from "utilities/focusError";
import { links } from "utilities/links";
import { Attributes } from "./Attributes/Attributes";
import { checkIfSchemaAreInEditMode } from "./utils";

type EditSchemaAttribute = SchemaAttribute & {
  isSaved?: boolean;
  isEditing?: boolean;
};

export interface EventSchemaFormValues {
  eventType: string;
  name: string;
  active: boolean;
  schema: {
    fields?: EditSchemaAttribute[];
  };
}

export const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    form: {
      width: "100%",
      maxWidth: "872px",
      margin: `${theme.spacing(8)} auto 0`,
    },
    displayColumn: { flexDirection: "column" },
    conditionSection: {
      borderBottom: `1px solid ${theme.gray100}`,
      maxWidth: "100%",
      padding: theme.spacing(12, 0, 16, 0),
      position: "relative",
      "&+&::after": {
        content: '""',
        left: 0,
        position: "absolute",
        top: 0,
        width: "100%",
      },
    },
    conditionWrapper: {
      alignItems: "flex-start",
      display: "flex",
      justifyContent: "space-between",
    },
    TooltipWrapper: {
      display: "flex",
      flexWrap: "wrap",
      gap: theme.spacing(12),
      minWidth: 0,
    },
    grayText: {
      color: theme.gray500,
      display: "block",
      fontSize: 18,
      margin: theme.spacing(12, 0, 8),
    },
    submitContainer: {
      alignItems: "center",
      justifyContent: "flex-end",
      display: "flex",
      marginBottom: theme.spacing(20),
    },
    conditionHeader: {
      display: "flex",
      fontSize: 18,
      justifyContent: "space-between",
      margin: theme.spacing(0, 6, 6),
    },
  }),
);

export function CustomEventSchema(props: {
  editMode?: boolean;
  initialValues?: EventSchemaFormValues;
  initialErrors?: FormikErrors<EventSchemaFormValues>;
  onSaveCallback?: (customEventSchema: EventSchemaPayload) => void;
  onCancelCallback?: () => void;
}) {
  const { message } = useMessage();
  const { notify } = useNotifications();
  const navigate = useNavigate();
  const { addCustomEventSchema, editCustomEventSchema } = useEvents();
  const { id: customEventSchemaID } = useParams<{ id: string }>();

  const initialValues: EventSchemaFormValues =
    (props.editMode && props.initialValues) || props.initialValues
      ? {
          ...props.initialValues,
          schema: {
            fields:
              props.initialValues.schema?.fields?.map((args) => ({
                ...args,
                isSaved: true,
                isEditing: false,
              })) || [],
          },
        }
      : {
          eventType: "",
          name: "",
          schema: {},
          active: false,
        };

  return (
    <>
      <Formik
        enableReinitialize
        validateOnChange={false}
        validateOnBlur={false}
        initialStatus={{}}
        initialValues={initialValues}
        initialErrors={props.initialErrors}
        onSubmit={async (values, { setSubmitting, setErrors }) => {
          const areSchemaInEditMode = checkIfSchemaAreInEditMode();

          if (areSchemaInEditMode) {
            return focusError();
          }
          setSubmitting(true);

          try {
            if (props.editMode && props.initialValues) {
              await editCustomEventSchema(customEventSchemaID as string, {
                ...(({ eventType, schema, active, ...rest }) => ({
                  ...rest,
                  schema: {
                    fields: schema?.fields?.map(
                      ({ isEditing, isSaved, ...rest }) => ({
                        ...rest,
                      }),
                    ),
                  },
                  active: {
                    isActive: active,
                  },
                }))(values),
              });
            } else {
              await addCustomEventSchema({
                ...(({ schema, active, ...rest }) => ({
                  ...rest,
                  schema: {
                    fields: schema?.fields?.map(
                      ({ isEditing, isSaved, ...rest }) => ({
                        ...rest,
                      }),
                    ),
                  },
                  active: {
                    isActive: active,
                  },
                }))(values),
              });
            }

            props.onSaveCallback
              ? props.onSaveCallback({
                  ...(({ schema, active, ...rest }) => ({
                    ...rest,
                    schema: {
                      fields: schema?.fields?.map(
                        ({ name, type, description }) => ({
                          name,
                          type,
                          description,
                        }),
                      ),
                    },
                    active: {
                      isActive: active,
                    },
                  }))(values),
                })
              : navigate(links.customEventsSchemaList);

            notify(
              message({
                id: props.editMode
                  ? "customEventSchemaUpdated"
                  : "customEventSchemaAdded",
              }),
            );
          } catch (exception) {
            setSubmitting(false);
            notify(getErrorMessage(exception));
            const errors = backendErrors(getBackendErrors(exception));
            setErrors(errors);
            focusError();
          }
        }}
      >
        {({
          dirty,
          errors,
          handleSubmit,
          isSubmitting,
          setFieldError,
          setFieldValue,
          status,
          values,
          initialValues,
        }) => {
          return (
            <Form onSubmit={handleSubmit}>
              <FormSection
                sectionError={status?.[""]}
                title={message({ id: "basicSettings" })}
              >
                <Grid container spacing={12}>
                  <Grid item xs={6}>
                    <TextInput
                      disabled={props.editMode}
                      label={
                        <RequiredFieldLabel
                          text={message({
                            id: "eventSystemIdentifier",
                          })}
                        />
                      }
                      name="eventType"
                      data-test-id="event-schema-event-system-identifier-input"
                      width="100%"
                      InputProps={{
                        endAdornment: (
                          <Tooltip
                            text={message({
                              id: "attributeSystemIdentifierInfo",
                            })}
                          >
                            <InfoCircleIcon />
                          </Tooltip>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextInput
                      label={
                        <RequiredFieldLabel
                          text={message({ id: "eventName" })}
                        />
                      }
                      name="name"
                      data-test-id="event-schema-event-name-input"
                      width="100%"
                      InputProps={{
                        endAdornment: (
                          <Tooltip
                            text={message({
                              id: "attributeNameInfo",
                            })}
                          >
                            <InfoCircleIcon />
                          </Tooltip>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </FormSection>

              <Attributes
                attributes={values.schema.fields}
                setFieldValue={setFieldValue}
                setFieldError={setFieldError}
                errors={errors?.schema}
              />

              <FormSection title={message({ id: "eventStatus" })}>
                <Toggle
                  label={message({ id: "active" })}
                  name="active"
                  data-test-id="active-event-schema"
                />
              </FormSection>
              <Box mt={12} />
              <FormButtons
                disableSubmit={isSubmitting}
                onCancelClick={() => {
                  if (props.onCancelCallback) {
                    return props.onCancelCallback();
                  }
                  navigate(links.customEventsSchemaList);
                }}
                onSubmitClick={() => handleSubmit()}
                {...(!props.editMode && { submitMessage: "addEventSchema" })}
              />
              {!isSubmitting && (
                <LeaveSiteGuard
                  warnBeforeRefresh
                  isDirty={dirty}
                  initialValues={initialValues}
                  currentValues={values}
                />
              )}
            </Form>
          );
        }}
      </Formik>
    </>
  );
}
